<script lang="ts">
import type { FormErrorEvent, FormInputEvents, FormSchema, FormSubmitEvent, FormProps as UFormProps } from '@nuxt/ui';
import type { MaybeElementRef } from '@vueuse/core';
import type { ThemeSlots } from '#layers/ui/types/tv';
import type { FormData } from '#ui/types';
import type { FormFieldProps } from './FormField.vue';
import { createContext, useForwardExpose, useForwardPropsEmits } from 'reka-ui';
import { tv } from 'tailwind-variants';

export const [injectFormContext, provideFormContext] = createContext<FormContext>('Form');

const formTheme = tv({
	slots: {
		layout: '',
	},
});

// DForm does not expose Nuxt UI's transform/nested props, so bind those generics
// to the upstream defaults: transformed submit payloads and a top-level form.
type UFormForwardProps<S extends FormSchema> = Pick<UFormProps<S, true, false>, 'schema' | 'validate' | 'validateOn' | 'state'>;

export interface FormProps<S extends FormSchema = FormSchema> {
	state?: UFormProps<S, true, false>['state'];
	schema?: S;
	validate?: UFormProps<S, true, false>['validate'];
	validateOn?: FormInputEvents[];

	layout?: FormFieldProps['layout'];
	layoutSplit?: FormFieldProps['layoutSplit'];
	autoFocusFirstInput?: boolean;
	scrollToInvalidField?: boolean;
	size?: FormFieldProps['size'];
	fieldUi?: FormFieldProps['ui'];
	ui?: ThemeSlots<typeof formTheme>;
}

export interface FormEmits<S extends FormSchema = unknown> {
	submit: [event: FormSubmitEvent<FormData<S>>];
	error: [event: FormErrorEvent];
}

export interface FormContext<T extends object = any> {
	layout: ComputedRef<FormProps<T>['layout']>;
	layoutSplit: ComputedRef<FormProps<T>['layoutSplit']>;
	size: ComputedRef<FormProps<T>['size']>;
	fieldUi: ComputedRef<FormProps<T>['fieldUi']>;
}
</script>

<script setup lang="ts" generic="S extends FormSchema">
import type { ComputedRef } from 'vue';
import { unrefElement } from '@vueuse/core';
import { computed, nextTick, onMounted, useTemplateRef } from 'vue';
import { focusFirst } from '#layers/ui/utils/autofocus';
import Form from '#ui/components/Form.vue';

const {
	validateOn = ['input', 'change'],
	layout = 'stacked',
	layoutSplit = '1-2',
	scrollToInvalidField = true,
	schema,
	validate,
	state,
	size,
	fieldUi,
	autoFocusFirstInput,
} = defineProps<FormProps<S>>();

const emit = defineEmits<FormEmits<S>>();

// Forward the schema by reference, not through reactivePick's deep reactive():
// Zod 4.5+ defines `_zod` as a read-only, non-configurable own property, and wrapping
// a schema in a reactive proxy makes Nuxt UI's `schema["~standard"].validate()` trip the
// JS proxy invariant. A plain computed keeps the schema (and state) raw while still
// reacting to prop changes.
const forwarded = useForwardPropsEmits(
	computed(() => ({
		schema,
		validate,
		validateOn,
		state,
	})),
	emit,
) as ComputedRef<UFormForwardProps<S>>;

const theme = formTheme();

const formLayoutRef = useTemplateRef('formLayout');

const parentFormContext = injectFormContext(null);

provideFormContext({
	layout: computed(() => layout ?? parentFormContext?.layout.value),
	layoutSplit: computed(() => layoutSplit ?? parentFormContext?.layoutSplit.value),
	size: computed(() => size ?? parentFormContext?.size.value),
	fieldUi: computed(() => fieldUi ?? parentFormContext?.fieldUi.value),
});

onMounted(maybeAutoFocusNextTick);

function maybeAutoFocusNextTick() {
	if (autoFocusFirstInput) {
		nextTick(() =>
			focusFirst(formLayoutRef as MaybeElementRef),
		);
	}
}

function scrollToFirstInvalidField() {
	if (!scrollToInvalidField) return;

	nextTick(() => {
		const form = unrefElement(formLayoutRef as MaybeElementRef);
		if (!form) return;

		const invalidEl = form.querySelector<HTMLElement>('[aria-invalid="true"]');
		if (!invalidEl) return;

		invalidEl.style.scrollMargin = '4rem'; // we can just set and forget about it, it's not important
		invalidEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
	});
}

const { forwardRef } = useForwardExpose();
</script>

<template>
	<Form :ref="forwardRef" v-bind="forwarded" @error="scrollToFirstInvalidField">
		<slot name="content-top" />

		<Suspense @resolve="maybeAutoFocusNextTick">
			<DFormLayout
				ref="formLayout"
				:class="theme.layout({ class: ui?.layout })"
			>
				<slot />
			</DFormLayout>
		</Suspense>

		<slot name="content-bottom" />
	</Form>
</template>
